/**
 * OTLP-compatible telemetry span types and builder functions.
 *
 * Uses ai.agent.* semantic conventions matching the backend at
 * packages/backend/app/services/telemetry_service.py.
 */

// -- Types --------------------------------------------------------------------

export interface SpanEvent {
  name: string;
  timestamp: number;
  attributes?: Record<string, string | number | boolean>;
}

export interface SpanStatus {
  code: number; // 0 = OK, 1 = UNSET, 2 = ERROR
  message?: string;
}

export interface TelemetrySpan {
  traceId: string; // 32-char hex
  spanId: string; // 16-char hex
  parentSpanId?: string;
  name: string;
  kind: "internal" | "client" | "server" | "producer" | "consumer";
  startTime: number; // unix epoch milliseconds
  endTime: number;
  attributes: Record<string, string | number | boolean>;
  status?: SpanStatus;
  events?: SpanEvent[];
}

// -- ID generation ------------------------------------------------------------

function randomHex(byteCount: number): string {
  const bytes = new Uint8Array(byteCount);
  crypto.getRandomValues(bytes);
  let hex = "";
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, "0");
  }
  return hex;
}

function generateTraceId(): string {
  return randomHex(16); // 16 bytes = 32 hex chars
}

function generateSpanId(): string {
  return randomHex(8); // 8 bytes = 16 hex chars
}

// -- Common options -----------------------------------------------------------

interface BaseSpanOpts {
  traceId?: string;
  spanId?: string;
  parentSpanId?: string;
  skillName?: string; // maps to ai.agent.skill.name
}

function applySkillName(
  attributes: Record<string, string | number | boolean>,
  skillName?: string,
): void {
  if (skillName) {
    attributes["ai.agent.skill.name"] = skillName;
  }
}

// -- buildLlmSpan -------------------------------------------------------------

export interface BuildLlmSpanOpts extends BaseSpanOpts {
  model: string;
  latencyMs: number;
  tokensInput: number;
  tokensOutput: number;
  provider?: string;
  costUsd?: number;
  status?: "ok" | "error";
  statusMessage?: string;
}

export function buildLlmSpan(opts: BuildLlmSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    // Legacy (keep for backward compat)
    "ai.agent.llm.model": opts.model,
    "ai.agent.llm.latency_ms": opts.latencyMs,
    "ai.agent.llm.tokens_input": opts.tokensInput,
    "ai.agent.llm.tokens_output": opts.tokensOutput,
    // OTel gen_ai standard
    "gen_ai.request.model": opts.model,
    "gen_ai.usage.input_tokens": opts.tokensInput,
    "gen_ai.usage.output_tokens": opts.tokensOutput,
    "gen_ai.operation.name": "chat",
  };
  if (opts.provider !== undefined) {
    attributes["ai.agent.llm.provider"] = opts.provider;
    attributes["gen_ai.system"] = opts.provider;
  }
  if (opts.costUsd !== undefined) {
    attributes["ai.agent.llm.cost_usd"] = opts.costUsd;
    attributes["gen_ai.usage.cost_usd"] = opts.costUsd;
  }
  applySkillName(attributes, opts.skillName);

  const isError = opts.status === "error";
  const status: SpanStatus = isError
    ? { code: 2, ...(opts.statusMessage ? { message: opts.statusMessage } : {}) }
    : { code: 0 };

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "llm.inference",
    kind: "internal",
    startTime: now - opts.latencyMs,
    endTime: now,
    attributes,
    status,
  };
}

// -- buildToolSpan ------------------------------------------------------------

export interface BuildToolSpanOpts extends BaseSpanOpts {
  toolName: string;
  latencyMs: number;
  success: boolean;
  errorMessage?: string;
}

export function buildToolSpan(opts: BuildToolSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "ai.agent.tool.name": opts.toolName,
    "ai.agent.tool.latency_ms": opts.latencyMs,
    "ai.agent.tool.success": opts.success,
    // OTel gen_ai standard
    "gen_ai.tool.name": opts.toolName,
  };
  applySkillName(attributes, opts.skillName);

  const status: SpanStatus = opts.success
    ? { code: 0 }
    : { code: 2, ...(opts.errorMessage ? { message: opts.errorMessage } : {}) };

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "tool.execute",
    kind: "internal",
    startTime: now - opts.latencyMs,
    endTime: now,
    attributes,
    status,
  };
}

// -- buildErrorSpan -----------------------------------------------------------

export interface BuildErrorSpanOpts extends BaseSpanOpts {
  errorType: string;
  errorMessage: string;
  spanKind?: TelemetrySpan["kind"];
}

export function buildErrorSpan(opts: BuildErrorSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "ai.agent.error.type": opts.errorType,
    "ai.agent.error.message": opts.errorMessage,
    // OTel standard
    "error.type": opts.errorType,
  };
  applySkillName(attributes, opts.skillName);

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "error",
    kind: opts.spanKind ?? "internal",
    startTime: now,
    endTime: now,
    attributes,
    status: { code: 2, message: opts.errorMessage },
  };
}

// -- buildHttpSpan ------------------------------------------------------------

export interface BuildHttpSpanOpts extends BaseSpanOpts {
  method: string;
  url: string;
  statusCode: number;
  latencyMs: number;
  status?: "ok" | "error";
  statusMessage?: string;
}

export function buildHttpSpan(opts: BuildHttpSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "ai.agent.http.method": opts.method,
    "ai.agent.http.url": opts.url,
    "ai.agent.http.status_code": opts.statusCode,
    "ai.agent.http.latency_ms": opts.latencyMs,
    // OTel HTTP standard
    "http.request.method": opts.method,
    "url.full": opts.url,
    "http.response.status_code": opts.statusCode,
  };
  applySkillName(attributes, opts.skillName);

  const isError = opts.status === "error" || opts.statusCode >= 400;
  const status: SpanStatus = isError
    ? { code: 2, ...(opts.statusMessage ? { message: opts.statusMessage } : {}) }
    : { code: 0 };

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "http.request",
    kind: "client",
    startTime: now - opts.latencyMs,
    endTime: now,
    attributes,
    status,
  };
}

// -- buildActionSpan ----------------------------------------------------------

export interface BuildActionSpanOpts extends BaseSpanOpts {
  actionType: string;
  target: string;
  latencyMs: number;
  success: boolean;
  statusMessage?: string;
}

export function buildActionSpan(opts: BuildActionSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "ai.agent.action.type": opts.actionType,
    "ai.agent.action.target": opts.target,
    "ai.agent.action.latency_ms": opts.latencyMs,
    "ai.agent.action.success": opts.success,
  };
  applySkillName(attributes, opts.skillName);

  const status: SpanStatus = opts.success
    ? { code: 0 }
    : { code: 2, ...(opts.statusMessage ? { message: opts.statusMessage } : {}) };

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "action.execute",
    kind: "internal",
    startTime: now - opts.latencyMs,
    endTime: now,
    attributes,
    status,
  };
}

// -- buildNavSpan -------------------------------------------------------------

export interface BuildNavSpanOpts extends BaseSpanOpts {
  toUrl: string;
  latencyMs: number;
  status?: "ok" | "error";
  statusMessage?: string;
}

export function buildNavSpan(opts: BuildNavSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "ai.agent.nav.to_url": opts.toUrl,
    "ai.agent.nav.latency_ms": opts.latencyMs,
  };
  applySkillName(attributes, opts.skillName);

  const isError = opts.status === "error";
  const status: SpanStatus = isError
    ? { code: 2, ...(opts.statusMessage ? { message: opts.statusMessage } : {}) }
    : { code: 0 };

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "nav.navigate",
    kind: "internal",
    startTime: now - opts.latencyMs,
    endTime: now,
    attributes,
    status,
  };
}

// -- buildTaskSpan ------------------------------------------------------------

export interface BuildTaskSpanOpts extends BaseSpanOpts {
  taskId: string;
  taskName: string;
  taskType?: string;
  status: "pending" | "running" | "completed" | "failed";
  latencyMs: number;
  stepsCount?: number;
  tokensUsed?: number;
  statusMessage?: string;
}

export function buildTaskSpan(opts: BuildTaskSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "ai.agent.task.id": opts.taskId,
    "ai.agent.task.name": opts.taskName,
    "ai.agent.task.status": opts.status,
    "gen_ai.operation.name": "task",
  };
  if (opts.taskType !== undefined) attributes["ai.agent.task.type"] = opts.taskType;
  if (opts.stepsCount !== undefined) attributes["ai.agent.task.steps_count"] = opts.stepsCount;
  if (opts.tokensUsed !== undefined) attributes["ai.agent.task.tokens_used"] = opts.tokensUsed;
  applySkillName(attributes, opts.skillName);

  const isError = opts.status === "failed";
  const status: SpanStatus = isError
    ? { code: 2, ...(opts.statusMessage ? { message: opts.statusMessage } : {}) }
    : { code: 0 };

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "task.execute",
    kind: "internal",
    startTime: now - opts.latencyMs,
    endTime: now,
    attributes,
    status,
  };
}

// -- buildSkillInvocationSpan -------------------------------------------------

export interface BuildSkillInvocationSpanOpts extends BaseSpanOpts {
  invocationId: string;        // unique invocation ID
  phase: "start" | "processing" | "completion";
  version?: string;            // skill version semver
  inputTokens?: number;
  outputTokens?: number;
  costCents?: number;
  toolsUsed?: string[];        // tool names used during invocation
  schemaMatch?: boolean;       // whether output matched declared schema
  latencyMs: number;
  status?: "ok" | "error";
  statusMessage?: string;
}

export function buildSkillInvocationSpan(opts: BuildSkillInvocationSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "ai.agent.skill.invocation.id": opts.invocationId,
    "ai.agent.skill.phase": opts.phase,
  };
  if (opts.skillName) attributes["ai.agent.skill.name"] = opts.skillName;
  if (opts.version !== undefined) attributes["ai.agent.skill.version"] = opts.version;
  if (opts.inputTokens !== undefined) attributes["ai.agent.skill.input_tokens"] = opts.inputTokens;
  if (opts.outputTokens !== undefined) attributes["ai.agent.skill.output_tokens"] = opts.outputTokens;
  if (opts.costCents !== undefined) attributes["ai.agent.skill.cost_cents"] = opts.costCents;
  if (opts.toolsUsed !== undefined) attributes["ai.agent.skill.tools_used"] = opts.toolsUsed.join(",");
  if (opts.schemaMatch !== undefined) attributes["ai.agent.skill.schema_match"] = opts.schemaMatch;

  const isError = opts.status === "error";
  const status: SpanStatus = isError
    ? { code: 2, ...(opts.statusMessage ? { message: opts.statusMessage } : {}) }
    : { code: 0 };

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "skill.invoke",
    kind: "internal",
    startTime: now - opts.latencyMs,
    endTime: now,
    attributes,
    status,
  };
}

// -- buildEvalSpan ------------------------------------------------------------

export interface BuildEvalSpanOpts extends BaseSpanOpts {
  evaluationName: string;      // e.g., "tool_selection", "response_relevance"
  scoreValue: number;          // 0.0 - 1.0
  scoreLabel?: string;         // e.g., "accuracy", "pass/fail"
  explanation?: string;        // optional reasoning
  targetSpanId?: string;       // span being evaluated
}

export function buildEvalSpan(opts: BuildEvalSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "gen_ai.evaluation.name": opts.evaluationName,
    "gen_ai.evaluation.score.value": opts.scoreValue,
  };
  if (opts.scoreLabel) attributes["gen_ai.evaluation.score.label"] = opts.scoreLabel;
  if (opts.explanation) attributes["gen_ai.evaluation.explanation"] = opts.explanation;
  if (opts.targetSpanId) attributes["gen_ai.evaluation.target_span_id"] = opts.targetSpanId;
  applySkillName(attributes, opts.skillName);

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "gen_ai.evaluation",
    kind: "internal",
    startTime: now,
    endTime: now,
    attributes,
    status: { code: 0 },
  };
}

// -- buildForgeSpan -----------------------------------------------------------

export interface BuildForgeSpanOpts extends BaseSpanOpts {
  forgeSessionId: string;
  phase: "create" | "save_stage" | "brainstorm" | "build" | "publish" | "abandon";
  stageNumber?: number;
  latencyMs: number;
  status?: "ok" | "error";
  statusMessage?: string;
  artifactCount?: number;
  scanStatus?: string;
}

export function buildForgeSpan(opts: BuildForgeSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "ai.agent.forge.session_id": opts.forgeSessionId,
    "ai.agent.forge.phase": opts.phase,
  };
  if (opts.stageNumber !== undefined) attributes["ai.agent.forge.stage_number"] = opts.stageNumber;
  if (opts.artifactCount !== undefined) attributes["ai.agent.forge.artifact_count"] = opts.artifactCount;
  if (opts.scanStatus !== undefined) attributes["ai.agent.forge.scan_status"] = opts.scanStatus;
  applySkillName(attributes, opts.skillName);

  const isError = opts.status === "error";
  const status: SpanStatus = isError
    ? { code: 2, ...(opts.statusMessage ? { message: opts.statusMessage } : {}) }
    : { code: 0 };

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: `forge.${opts.phase}`,
    kind: "internal",
    startTime: now - opts.latencyMs,
    endTime: now,
    attributes,
    status,
  };
}

// -- buildPolicyViolationSpan (av.policy.evaluate) ----------------------------

export interface BuildPolicyViolationSpanOpts extends BaseSpanOpts {
  ruleId: string;
  policyScope: string;            // "tool" | "model" | "rate" | "network" | "custom"
  actionTaken: string;            // "block" | "warn" | "log"
  violationType: string;          // e.g. "forbidden_tool", "model_not_allowed"
  targetTool?: string;
  targetModel?: string;
  messageType?: string;
}

export function buildPolicyViolationSpan(opts: BuildPolicyViolationSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "av.policy.rule_id": opts.ruleId,
    "av.policy.scope": opts.policyScope,
    "av.policy.action_taken": opts.actionTaken,
    "av.policy.violation_type": opts.violationType,
  };
  if (opts.targetTool) attributes["av.policy.target_tool"] = opts.targetTool;
  if (opts.targetModel) attributes["av.policy.target_model"] = opts.targetModel;
  if (opts.messageType) attributes["av.policy.message_type"] = opts.messageType;
  applySkillName(attributes, opts.skillName);

  const isBlock = opts.actionTaken === "block";

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "av.policy.evaluate",
    kind: "internal",
    startTime: now,
    endTime: now,
    attributes,
    status: isBlock ? { code: 2, message: `Policy violation: ${opts.violationType}` } : { code: 0 },
  };
}

// -- buildDecisionSpan (av.decision) ------------------------------------------

export interface BuildDecisionSpanOpts extends BaseSpanOpts {
  decisionId: string;
  phase: "request" | "response" | "timeout" | "cancel" | "dismiss";
  priority?: "low" | "normal" | "high" | "urgent";
  category?: string;
  latencyMs: number;
  status?: "ok" | "error";
  statusMessage?: string;
}

export function buildDecisionSpan(opts: BuildDecisionSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "av.decision.id": opts.decisionId,
    "av.decision.phase": opts.phase,
  };
  if (opts.priority) attributes["av.decision.priority"] = opts.priority;
  if (opts.category) attributes["av.decision.category"] = opts.category;
  applySkillName(attributes, opts.skillName);

  const isError = opts.status === "error";
  const status: SpanStatus = isError
    ? { code: 2, ...(opts.statusMessage ? { message: opts.statusMessage } : {}) }
    : { code: 0 };

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "av.decision",
    kind: "internal",
    startTime: now - opts.latencyMs,
    endTime: now,
    attributes,
    status,
  };
}

// -- buildResyncSpan (av.resync) ----------------------------------------------

export interface BuildResyncSpanOpts extends BaseSpanOpts {
  conversationId: string;
  phase: "request" | "ack" | "complete" | "fail";
  reason?: string;                  // "ratchet_mismatch" | "key_loss" | "manual"
  latencyMs: number;
  status?: "ok" | "error";
  statusMessage?: string;
}

export function buildResyncSpan(opts: BuildResyncSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "av.resync.conversation_id": opts.conversationId,
    "av.resync.phase": opts.phase,
  };
  if (opts.reason) attributes["av.resync.reason"] = opts.reason;
  applySkillName(attributes, opts.skillName);

  const isError = opts.status === "error";
  const status: SpanStatus = isError
    ? { code: 2, ...(opts.statusMessage ? { message: opts.statusMessage } : {}) }
    : { code: 0 };

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "av.resync",
    kind: "internal",
    startTime: now - opts.latencyMs,
    endTime: now,
    attributes,
    status,
  };
}

// -- buildA2aSpan (av.a2a) ----------------------------------------------------

export interface BuildA2aSpanOpts extends BaseSpanOpts {
  channelId: string;
  operation: "request" | "approve" | "activate" | "message" | "close";
  peerHubAddress?: string;
  role?: "initiator" | "responder";
  latencyMs: number;
  status?: "ok" | "error";
  statusMessage?: string;
}

export function buildA2aSpan(opts: BuildA2aSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "av.a2a.channel_id": opts.channelId,
    "av.a2a.operation": opts.operation,
  };
  if (opts.peerHubAddress) attributes["av.a2a.peer_hub_address"] = opts.peerHubAddress;
  if (opts.role) attributes["av.a2a.role"] = opts.role;
  applySkillName(attributes, opts.skillName);

  const isError = opts.status === "error";
  const status: SpanStatus = isError
    ? { code: 2, ...(opts.statusMessage ? { message: opts.statusMessage } : {}) }
    : { code: 0 };

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "av.a2a",
    kind: "internal",
    startTime: now - opts.latencyMs,
    endTime: now,
    attributes,
    status,
  };
}

// -- buildScanSpan (av.scan) --------------------------------------------------

export interface BuildScanSpanOpts extends BaseSpanOpts {
  scanTarget: string;               // file path or content identifier
  scanType: "policy" | "content" | "workspace" | "skill";
  violationCount: number;
  ruleCount: number;
  latencyMs: number;
  status?: "ok" | "error";
  statusMessage?: string;
}

export function buildScanSpan(opts: BuildScanSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "av.scan.target": opts.scanTarget,
    "av.scan.type": opts.scanType,
    "av.scan.violation_count": opts.violationCount,
    "av.scan.rule_count": opts.ruleCount,
  };
  applySkillName(attributes, opts.skillName);

  const isError = opts.status === "error" || opts.violationCount > 0;
  const status: SpanStatus = isError
    ? { code: 2, ...(opts.statusMessage ? { message: opts.statusMessage } : {}) }
    : { code: 0 };

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "av.scan",
    kind: "internal",
    startTime: now - opts.latencyMs,
    endTime: now,
    attributes,
    status,
  };
}

// -- buildWorkspaceSpan (av.workspace) ----------------------------------------

export interface BuildWorkspaceSpanOpts extends BaseSpanOpts {
  workspaceId: string;
  operation: "upload" | "download" | "delete" | "list" | "scan";
  fileName?: string;
  fileSizeBytes?: number;
  encrypted?: boolean;
  latencyMs: number;
  status?: "ok" | "error";
  statusMessage?: string;
}

export function buildWorkspaceSpan(opts: BuildWorkspaceSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "av.workspace.id": opts.workspaceId,
    "av.workspace.operation": opts.operation,
  };
  if (opts.fileName) attributes["av.workspace.file_name"] = opts.fileName;
  if (opts.fileSizeBytes !== undefined) attributes["av.workspace.file_size_bytes"] = opts.fileSizeBytes;
  if (opts.encrypted !== undefined) attributes["av.workspace.encrypted"] = opts.encrypted;
  applySkillName(attributes, opts.skillName);

  const isError = opts.status === "error";
  const status: SpanStatus = isError
    ? { code: 2, ...(opts.statusMessage ? { message: opts.statusMessage } : {}) }
    : { code: 0 };

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "av.workspace",
    kind: "internal",
    startTime: now - opts.latencyMs,
    endTime: now,
    attributes,
    status,
  };
}

// -- buildCapabilitySpan (av.skill.invoke) ------------------------------------

export interface BuildCapabilitySpanOpts extends BaseSpanOpts {
  operation: "register" | "invoke" | "unregister" | "publish";
  version?: string;
  certificationTier?: string;
  toolsUsed?: string[];
  latencyMs: number;
  success: boolean;
  statusMessage?: string;
}

export function buildCapabilitySpan(opts: BuildCapabilitySpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "av.skill.operation": opts.operation,
    "av.skill.success": opts.success,
  };
  if (opts.skillName) attributes["av.skill.name"] = opts.skillName;
  if (opts.version) attributes["av.skill.version"] = opts.version;
  if (opts.certificationTier) attributes["av.skill.certification_tier"] = opts.certificationTier;
  if (opts.toolsUsed?.length) attributes["av.skill.tools_used"] = opts.toolsUsed.join(",");

  const status: SpanStatus = opts.success
    ? { code: 0 }
    : { code: 2, ...(opts.statusMessage ? { message: opts.statusMessage } : {}) };

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "av.skill.invoke",
    kind: "internal",
    startTime: now - opts.latencyMs,
    endTime: now,
    attributes,
    status,
  };
}

// -- buildEnrollmentSpan (av.enrollment) --------------------------------------

export interface BuildEnrollmentSpanOpts extends BaseSpanOpts {
  deviceId?: string;
  phase: "start" | "key_exchange" | "activate" | "complete" | "fail";
  enrollmentType?: "initial" | "re-enrollment" | "multi-device";
  latencyMs: number;
  status?: "ok" | "error";
  statusMessage?: string;
}

export function buildEnrollmentSpan(opts: BuildEnrollmentSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "av.enrollment.phase": opts.phase,
  };
  if (opts.deviceId) attributes["av.enrollment.device_id"] = opts.deviceId;
  if (opts.enrollmentType) attributes["av.enrollment.type"] = opts.enrollmentType;
  applySkillName(attributes, opts.skillName);

  const isError = opts.status === "error" || opts.phase === "fail";
  const status: SpanStatus = isError
    ? { code: 2, ...(opts.statusMessage ? { message: opts.statusMessage } : {}) }
    : { code: 0 };

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "av.enrollment",
    kind: "internal",
    startTime: now - opts.latencyMs,
    endTime: now,
    attributes,
    status,
  };
}

// -- buildRoomSpan (av.room) --------------------------------------------------

export interface BuildRoomSpanOpts extends BaseSpanOpts {
  roomId: string;
  operation: "create" | "join" | "leave" | "rekey" | "message" | "invite";
  roomName?: string;
  memberCount?: number;
  latencyMs: number;
  status?: "ok" | "error";
  statusMessage?: string;
}

export function buildRoomSpan(opts: BuildRoomSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "av.room.id": opts.roomId,
    "av.room.operation": opts.operation,
  };
  if (opts.roomName) attributes["av.room.name"] = opts.roomName;
  if (opts.memberCount !== undefined) attributes["av.room.member_count"] = opts.memberCount;
  applySkillName(attributes, opts.skillName);

  const isError = opts.status === "error";
  const status: SpanStatus = isError
    ? { code: 2, ...(opts.statusMessage ? { message: opts.statusMessage } : {}) }
    : { code: 0 };

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "av.room",
    kind: "internal",
    startTime: now - opts.latencyMs,
    endTime: now,
    attributes,
    status,
  };
}

// -- buildTrustSpan (av.trust) ------------------------------------------------

export interface BuildTrustSpanOpts extends BaseSpanOpts {
  agentHubAddress: string;
  operation: "score_update" | "tier_change" | "dimension_update" | "report_generate";
  previousTier?: string;
  newTier?: string;
  overallScore?: number;
  dimension?: string;
  dimensionScore?: number;
  latencyMs: number;
  status?: "ok" | "error";
  statusMessage?: string;
}

export function buildTrustSpan(opts: BuildTrustSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "av.trust.agent_hub_address": opts.agentHubAddress,
    "av.trust.operation": opts.operation,
  };
  if (opts.previousTier) attributes["av.trust.previous_tier"] = opts.previousTier;
  if (opts.newTier) attributes["av.trust.new_tier"] = opts.newTier;
  if (opts.overallScore !== undefined) attributes["av.trust.overall_score"] = opts.overallScore;
  if (opts.dimension) attributes["av.trust.dimension"] = opts.dimension;
  if (opts.dimensionScore !== undefined) attributes["av.trust.dimension_score"] = opts.dimensionScore;
  applySkillName(attributes, opts.skillName);

  const isError = opts.status === "error";
  const status: SpanStatus = isError
    ? { code: 2, ...(opts.statusMessage ? { message: opts.statusMessage } : {}) }
    : { code: 0 };

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "av.trust",
    kind: "internal",
    startTime: now - opts.latencyMs,
    endTime: now,
    attributes,
    status,
  };
}

// -- Eval Run Span (#21) ------------------------------------------------------

export interface BuildEvalRunSpanOpts {
  scenarioId: string;
  scenarioName?: string;
  variationType?: string;
  agentId: string;
  status: "pending" | "running" | "completed" | "failed";
  shiftMagnitude?: number;
  outcomeMatch?: boolean;
  flagged?: boolean;
  flagReason?: string;
  latencyMs: number;
  traceId?: string;
  spanId?: string;
  parentSpanId?: string;
  statusMessage?: string;
}

/**
 * Build an av.eval.run span for factorial evaluation execution telemetry.
 *
 * Tracks scenario-based evaluation runs, including shift measurement
 * (how much output changed under variation) and outcome matching.
 */
export function buildEvalRunSpan(opts: BuildEvalRunSpanOpts): TelemetrySpan {
  const now = Date.now();
  const attributes: Record<string, string | number | boolean> = {
    "av.eval.scenario_id": opts.scenarioId,
    "av.eval.agent_id": opts.agentId,
    "av.eval.status": opts.status,
    "av.eval.latency_ms": opts.latencyMs,
  };

  if (opts.scenarioName) attributes["av.eval.scenario_name"] = opts.scenarioName;
  if (opts.variationType) attributes["av.eval.variation_type"] = opts.variationType;
  if (opts.shiftMagnitude !== undefined)
    attributes["av.eval.shift_magnitude"] = opts.shiftMagnitude;
  if (opts.outcomeMatch !== undefined)
    attributes["av.eval.outcome_match"] = opts.outcomeMatch;
  if (opts.flagged !== undefined) attributes["av.eval.flagged"] = opts.flagged;
  if (opts.flagReason) attributes["av.eval.flag_reason"] = opts.flagReason;

  const isError = opts.status === "failed";
  const status: SpanStatus = isError
    ? { code: 2, ...(opts.statusMessage ? { message: opts.statusMessage } : {}) }
    : { code: 0 };

  return {
    traceId: opts.traceId ?? generateTraceId(),
    spanId: opts.spanId ?? generateSpanId(),
    parentSpanId: opts.parentSpanId,
    name: "av.eval.run",
    kind: "internal",
    startTime: now - opts.latencyMs,
    endTime: now,
    attributes,
    status,
  };
}

// -- InstrumentationContext ---------------------------------------------------

export interface InstrumentationContext {
  reportLlm: (opts: Omit<BuildLlmSpanOpts, "traceId" | "parentSpanId">) => void;
  reportTool: (opts: Omit<BuildToolSpanOpts, "traceId" | "parentSpanId">) => void;
  reportError: (opts: Omit<BuildErrorSpanOpts, "traceId" | "parentSpanId">) => void;
  reportHttp: (opts: Omit<BuildHttpSpanOpts, "traceId" | "parentSpanId">) => void;
  reportAction: (opts: Omit<BuildActionSpanOpts, "traceId" | "parentSpanId">) => void;
  reportNav: (opts: Omit<BuildNavSpanOpts, "traceId" | "parentSpanId">) => void;
  reportEval: (opts: Omit<BuildEvalSpanOpts, "traceId" | "parentSpanId">) => void;
  reportTask: (opts: Omit<BuildTaskSpanOpts, "traceId" | "parentSpanId">) => void;
  reportSkillInvocation: (opts: Omit<BuildSkillInvocationSpanOpts, "traceId" | "parentSpanId">) => void;
  skillName?: string; // propagated to all spans from this context
  traceId: string;
  parentSpanId: string;
}

// -- W3C TraceContext ---------------------------------------------------------

/**
 * W3C Trace Context headers for cross-agent/cross-sandbox trace correlation.
 *
 * @see https://www.w3.org/TR/trace-context/
 */
export interface TraceContext {
  /** W3C traceparent header: version-traceId-parentId-traceFlags */
  traceparent: string;
  /** W3C tracestate header (optional vendor-specific data) */
  tracestate?: string;
}

/**
 * Build a W3C traceparent header from a span.
 *
 * Format: `{version}-{trace-id}-{parent-id}-{trace-flags}`
 * - version: `00` (current spec)
 * - trace-id: 32 hex chars
 * - parent-id: 16 hex chars (the span's own spanId becomes parent for children)
 * - trace-flags: `01` (sampled)
 */
export function buildTraceparent(span: TelemetrySpan): string {
  return `00-${span.traceId}-${span.spanId}-01`;
}

/**
 * Parse a W3C traceparent header into its components.
 *
 * @returns null if the header is malformed
 */
export function parseTraceparent(header: string): {
  version: string;
  traceId: string;
  parentId: string;
  traceFlags: string;
} | null {
  const parts = header.split("-");
  if (parts.length !== 4) return null;
  const [version, traceId, parentId, traceFlags] = parts;
  if (!version || !traceId || !parentId || !traceFlags) return null;
  if (traceId.length !== 32 || parentId.length !== 16) return null;
  return { version, traceId, parentId, traceFlags };
}

/**
 * Create a TraceContext from a span for propagation across agent boundaries.
 */
export function spanToTraceContext(
  span: TelemetrySpan,
  tracestate?: string,
): TraceContext {
  return {
    traceparent: buildTraceparent(span),
    tracestate: tracestate ?? `av=s:${span.spanId}`,
  };
}

/**
 * Propagate trace context across agent/sandbox boundaries.
 *
 * Creates a child span context from an incoming traceparent header,
 * preserving the trace ID while creating a new span ID.
 */
export function propagateContext(
  incomingTraceparent: string,
): { traceId: string; parentSpanId: string; spanId: string } | null {
  const parsed = parseTraceparent(incomingTraceparent);
  if (!parsed) return null;

  return {
    traceId: parsed.traceId,
    parentSpanId: parsed.parentId,
    spanId: generateSpanId(),
  };
}

/**
 * Generate W3C Trace Context HTTP headers from a span for attachment
 * to outbound requests at transport boundaries (e.g., agent-to-agent,
 * cross-sandbox, OTLP export).
 *
 * @returns Record suitable for spreading into a fetch headers object
 */
export function spanToW3CHeaders(
  span: TelemetrySpan,
  tracestate?: string,
): Record<string, string> {
  return {
    traceparent: buildTraceparent(span),
    tracestate: tracestate ?? `av=s:${span.spanId}`,
  };
}
